import { type INotification } from '@/types/INotification';
import { isNotification } from '@/utils/isNotification';
import { isSameLocalDay } from '@/utils/isSameLocalDay';

export const NOTIFICATIONS_HISTORY_KEY = 'notificationsHistory';

const readList = (): INotification[] => {
  const raw = localStorage.getItem(NOTIFICATIONS_HISTORY_KEY);

  if (raw === null) {
    return [];
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }

  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.filter(isNotification);
};

const writeList = (list: INotification[]): void => {
  localStorage.setItem(NOTIFICATIONS_HISTORY_KEY, JSON.stringify(list));
};

/** Оставляет только уведомления текущего локального календарного дня. */
const dropStale = (list: INotification[]): INotification[] => {
  const now = Date.now();
  return list.filter((item) => isSameLocalDay(item.createdAt, now));
};

/**
 * Возвращает историю уведомлений за сегодняшний локальный день. Записи
 * прошлых дней попутно удаляются из хранилища (ленивая суточная чистка).
 *
 * @returns массив уведомлений текущего дня (новые сверху)
 */
export const getNotificationsHistory = (): INotification[] => {
  const fresh = dropStale(readList());
  writeList(fresh);
  return fresh;
};

/**
 * Добавляет уведомление в историю. Дубли по `id` игнорируются. Попутно
 * выполняется суточная чистка. Возвращает актуальный список после вставки.
 *
 * @param notification - уведомление для сохранения
 * @returns обновлённый список истории (новые сверху)
 */
export const addNotificationToHistory = (
  notification: INotification
): INotification[] => {
  const fresh = dropStale(readList());

  if (fresh.some((item) => item.id === notification.id)) {
    return fresh;
  }

  const next = [notification, ...fresh];
  writeList(next);
  return next;
};

/**
 * Помечает уведомление прочитанным в истории. Возвращает обновлённый список.
 *
 * @param id - идентификатор уведомления
 * @returns обновлённый список истории
 */
export const markNotificationRead = (id: string): INotification[] => {
  const next = dropStale(readList()).map((item) =>
    item.id === id ? { ...item, isRead: true } : item
  );
  writeList(next);
  return next;
};

/**
 * Помечает прочитанными все уведомления о заказе с указанным uuid. Связывает
 * «просмотр заказа» (открытие страницы заказа) с историей уведомлений.
 *
 * @param uuid - uuid заказа
 * @returns обновлённый список истории
 */
export const markOrderNotificationRead = (uuid: string): INotification[] => {
  const next = dropStale(readList()).map((item) =>
    item.type === 'order' && item.payload.data.uuid === uuid
      ? { ...item, isRead: true }
      : item
  );
  writeList(next);
  return next;
};

/**
 * Помечает все уведомления прочитанными. Возвращает обновлённый список.
 *
 * @returns обновлённый список истории
 */
export const markAllNotificationsRead = (): INotification[] => {
  const next = dropStale(readList()).map((item) => ({
    ...item,
    isRead: true
  }));
  writeList(next);
  return next;
};

/** Полностью очищает историю уведомлений (вызывается при логауте). */
export const clearNotificationsHistory = (): void => {
  localStorage.removeItem(NOTIFICATIONS_HISTORY_KEY);
};
